'use client';
import { SIZING_1_5, SIZING_2 } from '@mnd-frontend/ui/shared/tokens';
import styled from 'styled-components';
import { Spinner } from '../Spinner';

const FabContent = styled.span<{ $loading?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 100%;
  visibility: ${({ $loading }) => ($loading ? 'hidden' : 'visible')};
`;

const IconWrapper = styled.span`
  width: ${({ theme }) => theme.sizingIconXs};
  height: ${({ theme }) => theme.sizingIconXs};
  display: flex;
  align-items: center;
  justify-content: center;
  > * {
    width: 100%;
    height: 100%;
  }
`;

const SpinnerWrapper = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  line-height: 0;
  transform: translate(-50%, -50%);
`;

const Button = styled.button<{ $iconOnly?: boolean; $loading?: boolean }>`
  all: unset;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: ${props => (props.$iconOnly ? props.theme.sizingButtonWidthIconOnly : 'auto')};
  height: ${props => props.theme.sizingButtonHeight};
  cursor: pointer;
  color: ${props => props.theme.colorTextFabLabelDefault};
  background-color: ${props => props.theme.colorBackgroundFabDefault};
  border-radius: ${props => props.theme.radiusFab};
  padding: ${props => (props.$iconOnly ? '0' : `0 ${SIZING_1_5}`)};
  box-shadow: 0 2px 4px 0 rgba(49, 73, 164, 0.1);
  font-size: ${SIZING_2};
  font-weight: 500;
  position: relative;

  [data-whatintent='keyboard'] &:focus,
  &:hover {
    color: ${props => props.theme.colorTextFabLabelHover};
    background-color: ${props => props.theme.colorBackgroundFabHover};
  }

  &:active {
    color: ${props => props.theme.colorTextButtonTextFilledOnLightFocused};
    background-color: ${props => props.theme.colorBackgroundButtonFilledOnLightFocused};
  }

  &[aria-disabled='true'],
  &:disabled {
    pointer-events: none;
    cursor: default;
    color: ${props => props.theme.colorTextButtonTextFilledOnLightDisabled};
    background-color: ${props => props.theme.colorBackgroundButtonFilledOnLightDisabled};
  }
`;

type FabProps = {
  icon: React.ReactNode;
  label?: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
};

export const Fab = ({ label, icon, onClick, disabled, loading }: FabProps) => {
  const isIconOnly = !label || label.trim() === '';
  const isDisabled = disabled || loading;

  return (
    <Button
      onClick={onClick}
      $iconOnly={isIconOnly}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      $loading={loading}
    >
      <FabContent $loading={loading} aria-hidden={loading}>
        <IconWrapper>{icon}</IconWrapper>
        {label}
      </FabContent>
      {loading && (
        <SpinnerWrapper>
          <Spinner size="sm" aria-label="Loading..." />
        </SpinnerWrapper>
      )}
    </Button>
  );
};
